/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView, ScrollView, View, Text, StyleSheet,
  TouchableOpacity, Dimensions, FlatList, TextInput, Image,
  Modal, Alert, ActivityIndicator, RefreshControl, Switch,
  Platform, Share, KeyboardAvoidingView,
} from 'react-native';
import {
  Ionicons, MaterialCommunityIcons, FontAwesome5,
  AntDesign, MaterialIcons, Feather,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import * as DocumentPicker from 'expo-document-picker';

const { width } = Dimensions.get('window');

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const API_BASE  = 'https://api.jobeng.ai';
const API_KEY   = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const P         = '#0e4e96';
const P2        = '#1e7be2';
const Y         = '#fdb913';
const TEAL      = '#19abb5';

const getH = async () => {
  const tok = (await AsyncStorage.getItem('auth_token')) || (await AsyncStorage.getItem('token')) || '';
  return { 'Content-Type':'application/json','x-api-key':API_KEY,'Authorization':`Bearer ${tok}` };
};
const getBizId = async () => { const b = await AsyncStorage.getItem('company_id'); return parseInt(b)||0; };

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmtGBP  = v => `£${Number(v||0).toLocaleString('en-GB',{minimumFractionDigits:0,maximumFractionDigits:0})}`;
const fmtDate = d => { if(!d) return '—'; return new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}); };
const daysLeft = d => { if(!d) return null; return Math.ceil((new Date(d)-new Date())/(1000*60*60*24)); };
const uid = () => `_${Math.random().toString(36).substr(2,9)}`;

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  {id:'all',label:'All'},
  {id:'commercial',label:'Commercial'},
  {id:'residential',label:'Residential'},
  {id:'education',label:'Education'},
  {id:'healthcare',label:'Healthcare'},
  {id:'retail',label:'Retail'},
  {id:'infrastructure',label:'Infrastructure'},
  {id:'construction',label:'Construction'},
  {id:'engineering',label:'Engineering'},
  {id:'electrical',label:'Electrical'},
  {id:'plumbing',label:'Plumbing'},
  {id:'hvac',label:'HVAC'},
  {id:'it',label:'IT'},
  {id:'civil',label:'Civil'},
  {id:'maintenance',label:'Maintenance'},
  {id:'other',label:'Other'},
];

const STATUS_CONFIG = {
  open:         {label:'Open',          color:'#059669', bg:'#d1fae5'},
  draft:        {label:'Draft',         color:'#64748B', bg:'#f1f5f9'},
  under_review: {label:'Under Review',  color:'#d97706', bg:'#fef3c7'},
  awarded:      {label:'Awarded',       color:'#7c3aed', bg:'#ede9fe'},
  closed:       {label:'Closed',        color:'#64748B', bg:'#f1f5f9'},
  cancelled:    {label:'Cancelled',     color:'#dc2626', bg:'#fee2e2'},
  published:    {label:'Published',     color:'#059669', bg:'#d1fae5'},
};

const MAIN_TABS = ['All','Internal','External','Active','Drafts','Saved'];

// ─── SAMPLE DATA ──────────────────────────────────────────────────────────────
const SAMPLE_TENDERS = [
  {id:'t1',title:'New Office Building – Structural Steel Supply',type:'external',category:'commercial',status:'open',location:'Central London, UK',budgetMin:500000,budgetMax:750000,currency:'GBP',deadline:'2025-06-30',duration:'9 months',description:'Seeking bids for the supply and delivery of structural steel for a new 5-story office building. CE Marking required. Full fabrication drawings supplied.',requirements:['CE Marking certification','10+ years structural steel experience','Proven track record on commercial schemes','CSCS site cards for all operatives'],bond:true,bondAmount:50000,tags:['commercial','urgent'],contactEmail:'procurement@majordeveloper.co.uk',contactPhone:'+44 20 7946 0001',organisation:'Major Developer Inc.',postedBy:'Major Developer Inc.',type:'external',views:214,applicants:7,createdDate:'2025-03-01'},
  {id:'t2',title:'Residential Complex – Electrical Subcontracting',type:'internal',category:'residential',status:'open',location:'Manchester, UK',budgetMin:100000,budgetMax:150000,currency:'GBP',deadline:'2025-06-25',duration:'5 months',description:'Seeking bids from qualified electrical subcontractors for a new 50-unit residential complex. All first and second fix electrical installation, testing and commissioning.',requirements:['NICEIC certified','5+ years residential experience','Team capacity min 5 operatives','Own tools and transport'],bond:false,tags:['residential'],contactEmail:'projects@jobeng.ai',contactPhone:'+44 161 000 0000',organisation:'JOBENG',postedBy:'JOBENG',views:89,applicants:3,createdDate:'2025-03-05'},
  {id:'t3',title:'School Renovation – Plumbing Services',type:'internal',category:'education',status:'under_review',location:'Birmingham, UK',budgetMin:50000,budgetMax:75000,currency:'GBP',deadline:'2025-05-31',duration:'3 months',description:'Reviewing bids for plumbing services for a comprehensive school renovation. Includes heating upgrades, hot and cold water systems and sanitary installations.',requirements:['Gas Safe registered','DBS checked workforce','School project experience','JCT Sub-contract'],bond:false,tags:['education','public-sector'],contactEmail:'projects@jobeng.ai',organisation:'JOBENG',postedBy:'JOBENG',views:167,applicants:5,createdDate:'2025-02-15'},
  {id:'t4',title:'Retail Fit-out – HVAC Installation',type:'external',category:'retail',status:'awarded',location:'Leeds, UK',budgetMin:80000,budgetMax:100000,currency:'GBP',deadline:'2025-05-15',duration:'4 months',description:'Tender for HVAC installation for a new retail store fit-out. VRF system, fresh air supply, BMS integration.',requirements:['F-Gas certified engineers','Retail fit-out experience','Manufacturer approved installer'],bond:false,tags:['retail'],organisation:'National Retail Chain',postedBy:'National Retail Chain',views:55,applicants:4,createdDate:'2025-01-20'},
  {id:'t5',title:'Infrastructure Project – Concrete Supply',type:'external',category:'infrastructure',status:'closed',location:'Edinburgh, UK',budgetMin:1000000,budgetMax:1500000,currency:'GBP',deadline:'2025-04-30',duration:'18 months',description:'Tender for the supply of high-strength concrete for a major infrastructure project. Ready-mix and precast elements required.',requirements:['BSI Kitemark','High volume production capacity','Full logistics capability','CHAS accredited'],bond:true,bondAmount:100000,tags:['infrastructure','public-sector'],organisation:'City Council',postedBy:'City Council',views:412,applicants:12,createdDate:'2025-01-01'},
];

// ─── COMPANY LOGO HOOK ────────────────────────────────────────────────────────
const useCompanyLogo = () => {
  const [logoUrl, setLogoUrl] = useState(null);
  const [companyName, setCompanyName] = useState('JOBENG');
  useEffect(()=>{
    AsyncStorage.getItem('company_profile').then(raw=>{
      if(!raw) return;
      const p=JSON.parse(raw);
      setCompanyName(p.company_name||p.name||'JOBENG');
      const logo=p.logo||p.logo_url||p.company_logo||p.banner_image_url;
      if(logo && typeof logo === 'string' && logo.length > 0) {
        if(logo.startsWith('http') || logo.startsWith('data:')) setLogoUrl(logo);
        else if(logo.startsWith('/')) setLogoUrl(`${API_BASE}${logo}`);
      }
    }).catch(()=>{});
  },[]);
  return { logoUrl, companyName };
};

// ─── STATUS PILL ──────────────────────────────────────────────────────────────
const StatusPill = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <View style={[ss.pill,{backgroundColor:cfg.bg,borderColor:cfg.color}]}>
      <Text style={[ss.pillTxt,{color:cfg.color}]}>{cfg.label}</Text>
    </View>
  );
};

// ─── TENDER CARD ──────────────────────────────────────────────────────────────
const TenderCard = ({ item, onPress, onSave, saved, submittedBids }) => {
  const dl = daysLeft(item.deadline);
  const bidCount = submittedBids.filter(b=>b.tenderId===item.id).length;
  const isInternal = item.type==='internal';
  return (
    <TouchableOpacity style={ss.card} onPress={()=>onPress(item)} activeOpacity={0.85}>
      <View style={ss.cardTop}>
        <View style={[ss.typeStripe,{backgroundColor:isInternal?P:TEAL}]}>
          <Text style={ss.typeStripeTxt}>{isInternal?'INTERNAL':'EXTERNAL'}</Text>
        </View>
        <TouchableOpacity onPress={()=>onSave(item.id)} style={ss.saveBtn}>
          <MaterialCommunityIcons name={saved?'bookmark':'bookmark-outline'} size={22} color={saved?Y:P2}/>
        </TouchableOpacity>
      </View>
      <View style={ss.cardBody}>
        <View style={ss.cardTitleRow}>
          <Text style={ss.cardTitle} numberOfLines={2}>{item.title}</Text>
          <StatusPill status={item.status}/>
        </View>
        <Text style={ss.cardOrg} numberOfLines={1}><MaterialCommunityIcons name="office-building" size={12} color="#94a3b8"/> {item.organisation||item.postedBy||'—'}</Text>
        <View style={ss.tagsRow}>
          {item.tags?.slice(0,3).map(t=>(
            <View key={t} style={ss.tag}><Text style={ss.tagTxt}>{t.toUpperCase()}</Text></View>
          ))}
          {item.bond&&<View style={[ss.tag,{backgroundColor:'#fef3c7',borderColor:'#d97706'}]}><Text style={[ss.tagTxt,{color:'#d97706'}]}>BOND</Text></View>}
        </View>
        <View style={ss.metaGrid}>
          <View style={ss.metaItem}><MaterialCommunityIcons name="cash" size={13} color={P}/><Text style={ss.metaTxt}>{fmtGBP(item.budgetMin)}–{fmtGBP(item.budgetMax)}</Text></View>
          <View style={ss.metaItem}><MaterialCommunityIcons name="map-marker" size={13} color="#64748B"/><Text style={ss.metaTxt} numberOfLines={1}>{item.location||'—'}</Text></View>
          <View style={ss.metaItem}><MaterialCommunityIcons name="clock-outline" size={13} color={dl!=null&&dl<=7?'#dc2626':'#64748B'}/><Text style={[ss.metaTxt,dl!=null&&dl<=7&&{color:'#dc2626',fontWeight:'700'}]}>{dl!=null?(dl<=0?'CLOSED':`${dl}d left`):'—'}</Text></View>
          <View style={ss.metaItem}><MaterialCommunityIcons name="calendar" size={13} color="#64748B"/><Text style={ss.metaTxt}>{item.duration||'—'}</Text></View>
        </View>
        <View style={ss.cardFooter}>
          <View style={ss.statsRow}>
            {item.views>0&&<View style={ss.metaItem}><MaterialCommunityIcons name="eye" size={13} color="#94a3b8"/><Text style={[ss.metaTxt,{color:'#94a3b8'}]}>{item.views}</Text></View>}
            {isInternal&&<View style={ss.metaItem}><MaterialCommunityIcons name="send" size={13} color={P2}/><Text style={[ss.metaTxt,{color:P2,fontWeight:'700'}]}>{item.applicants||bidCount} bids</Text></View>}
          </View>
          <TouchableOpacity style={ss.viewBtn} onPress={()=>onPress(item)}>
            <Text style={ss.viewBtnTxt}>View Details</Text>
            <MaterialCommunityIcons name="chevron-right" size={14} color="#fff"/>
          </TouchableOpacity>
        </View>
      </View>
    </TouchableOpacity>
  );
};

// ─── MAIN SCREEN ──────────────────────────────────────────────────────────────
const TendersManagementScreen = ({ navigation, route }) => {
  const { logoUrl, companyName } = useCompanyLogo();

  const [tenders,       setTenders]       = useState([]);
  const [myTenders,     setMyTenders]     = useState([]);
  const [savedIds,      setSavedIds]      = useState([]);
  const [submittedBids, setSubmittedBids] = useState([]);

  const [activeTab,     setActiveTab]     = useState('All');
  const [searchQuery,   setSearchQuery]   = useState('');
  const [sortBy,        setSortBy]        = useState('deadline');
  const [filters,       setFilters]       = useState({category:'all',status:'all',type:'all',budgetMin:'',budgetMax:'',location:'',bond:false});

  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [previewTender, setPreviewTender] = useState(null);
  const [showPreview,   setShowPreview]   = useState(false);
  const [showForm,      setShowForm]      = useState(false);
  const [editingTender, setEditingTender] = useState(null);
  const [showFilter,    setShowFilter]    = useState(false);
  const [showSort,      setShowSort]      = useState(false);

  useEffect(()=>{ loadAll(); },[]);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadTenders(), loadStorage()]);
    setLoading(false);
  };
  const onRefresh = async () => { setRefreshing(true); await loadAll(); setRefreshing(false); };

  const loadTenders = async () => {
    try {
      const bizId = await getBizId(); const h = await getH();
      const res = await fetch(`${API_BASE}/api/data/tenders?company_id=${bizId}&limit=200`,{headers:h});
      if(res.ok){ const d=await res.json(); setTenders(d.tenders||d.data||SAMPLE_TENDERS); return; }
    } catch {}
    setTenders(SAMPLE_TENDERS);
  };

  const loadStorage = async () => {
    try {
      const rm = await AsyncStorage.getItem('my_tenders');     if(rm) setMyTenders(JSON.parse(rm));
      const rs = await AsyncStorage.getItem('saved_tenders');  if(rs) setSavedIds(JSON.parse(rs));
      const rb = await AsyncStorage.getItem('submitted_bids'); if(rb) setSubmittedBids(JSON.parse(rb));
    } catch {}
  };

  // ─── DATA HELPERS ──────────────────────────────────────────────────────────
  const allTenders = [...tenders, ...myTenders.filter(m=>!tenders.find(t=>t.id===m.id))];

  const getFiltered = useCallback(() => {
    let base = [...allTenders];
    if (activeTab==='Internal') base=base.filter(t=>t.type==='internal'||t.type==='Internal');
    else if (activeTab==='External') base=base.filter(t=>t.type==='external'||t.type==='External');
    else if (activeTab==='Active') base=base.filter(t=>['open','published','under_review'].includes((t.status||'').toLowerCase()));
    else if (activeTab==='Drafts') base=base.filter(t=>(t.status||'').toLowerCase()==='draft');
    else if (activeTab==='Saved') base=base.filter(t=>savedIds.includes(t.id));

    if (searchQuery) {
      const q=searchQuery.toLowerCase();
      base=base.filter(t=>(t.title+t.organisation+t.location+t.description).toLowerCase().includes(q));
    }
    if (filters.category!=='all') base=base.filter(t=>(t.category||'').toLowerCase()===filters.category);
    if (filters.status!=='all')   base=base.filter(t=>(t.status||'').toLowerCase()===filters.status);
    if (filters.type!=='all')     base=base.filter(t=>(t.type||'').toLowerCase()===filters.type);
    if (filters.budgetMin)        base=base.filter(t=>(t.budgetMax||0)>=Number(filters.budgetMin));
    if (filters.budgetMax)        base=base.filter(t=>(t.budgetMin||0)<=Number(filters.budgetMax));
    if (filters.location)         base=base.filter(t=>(t.location||'').toLowerCase().includes(String(filters.location ?? '').toLowerCase()));
    if (filters.bond)             base=base.filter(t=>t.bond);

    return base.sort((a,b)=>{
      if(sortBy==='deadline')  return new Date(a.deadline||'9999')-new Date(b.deadline||'9999');
      if(sortBy==='budget_h')  return (b.budgetMax||0)-(a.budgetMax||0);
      if(sortBy==='budget_l')  return (a.budgetMin||0)-(b.budgetMin||0);
      if(sortBy==='newest')    return new Date(b.createdDate||0)-new Date(a.createdDate||0);
      if(sortBy==='applicants')return (b.applicants||0)-(a.applicants||0);
      return 0;
    });
  }, [allTenders, activeTab, searchQuery, filters, sortBy, savedIds]);

  const filtered = getFiltered();

  // ─── STATS ─────────────────────────────────────────────────────────────────
  const totalOpen     = allTenders.filter(t=>['open','published'].includes((t.status||'').toLowerCase())).length;
  const totalInternal = allTenders.filter(t=>['internal','Internal'].includes(t.type)).length;
  const totalExternal = allTenders.filter(t=>['external','External'].includes(t.type)).length;
  const totalAwarded  = allTenders.filter(t=>(t.status||'').toLowerCase()==='awarded').length;
  const totalBudget   = allTenders.filter(t=>['open','published'].includes((t.status||'').toLowerCase())).reduce((s,t)=>s+(t.budgetMax||0),0);

  // ─── CRUD ──────────────────────────────────────────────────────────────────
  const saveTender = async (data, publish) => {
    try {
      const bizId=await getBizId(); const h=await getH();
      const payload={...data,company_id:bizId,status:publish?'published':'draft',createdDate:data.createdDate||new Date().toISOString()};
      if(data.id){
        try{ await fetch(`${API_BASE}/api/data/tenders/${data.id}`,{method:'PUT',headers:h,body:JSON.stringify(payload)}); }catch{}
        const upd=myTenders.map(t=>t.id===data.id?payload:t);
        setMyTenders(upd); await AsyncStorage.setItem('my_tenders',JSON.stringify(upd));
      } else {
        payload.id=uid();
        try{ await fetch(`${API_BASE}/api/data/tenders`,{method:'POST',headers:h,body:JSON.stringify(payload)}); }catch{}
        const upd=[payload,...myTenders];
        setMyTenders(upd); await AsyncStorage.setItem('my_tenders',JSON.stringify(upd));
      }
      Alert.alert('Success', publish?'Tender published successfully!':'Tender saved as draft.');
    } catch { Alert.alert('Error','Failed to save tender.'); }
  };

  const deleteTender = async (id) => {
    Alert.alert('Delete Tender','This action cannot be undone.',[{text:'Cancel'},{text:'Delete',style:'destructive',onPress:async()=>{
      try{ const h=await getH(); await fetch(`${API_BASE}/api/data/tenders/${id}`,{method:'DELETE',headers:h}); }catch{}
      const upd=myTenders.filter(t=>t.id!==id);
      setMyTenders(upd); await AsyncStorage.setItem('my_tenders',JSON.stringify(upd));
      setTenders(prev=>prev.filter(t=>t.id!==id));
    }}]);
  };

  const closeTender = async (tender) => {
    Alert.alert('Close Tender','Are you sure?',[{text:'Cancel'},{text:'Close',style:'destructive',onPress:async()=>{
      const updated={...tender,status:'closed'};
      try{ const h=await getH(); await fetch(`${API_BASE}/api/data/tenders/${tender.id}`,{method:'PUT',headers:h,body:JSON.stringify(updated)}); }catch{}
      const upd=myTenders.map(t=>t.id===tender.id?updated:t);
      setMyTenders(upd); await AsyncStorage.setItem('my_tenders',JSON.stringify(upd));
      setPreviewTender(updated);
    }}]);
  };

  const toggleSave = async (id) => {
    const ns=savedIds.includes(id)?savedIds.filter(s=>s!==id):[...savedIds,id];
    setSavedIds(ns); await AsyncStorage.setItem('saved_tenders',JSON.stringify(ns));
  };

  // ─── PDF / PRINT ──────────────────────────────────────────────────────────
  const buildHTML = (t) => {
    const dl = daysLeft(t.deadline);
    const status = STATUS_CONFIG[(t.status||'draft').toLowerCase()]||STATUS_CONFIG.draft;
    return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:Arial,sans-serif;color:#1e293b;background:#fff;}
  .header{background:linear-gradient(135deg,#0e4e96 0%,#1e7be2 100%);padding:36px 32px;color:#fff;}
  .header-top{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;}
  .brand{font-size:13px;opacity:0.8;letter-spacing:2px;text-transform:uppercase;}
  .tender-ref{background:rgba(255,255,255,0.15);border-radius:8px;padding:8px 14px;text-align:right;}
  .tender-ref .label{font-size:10px;opacity:0.8;}
  .tender-ref .value{font-size:16px;font-weight:700;}
  h1{font-size:24px;font-weight:800;margin-bottom:6px;line-height:1.3;}
  .org{font-size:14px;opacity:0.85;margin-bottom:14px;}
  .badges{display:flex;gap:8px;flex-wrap:wrap;}
  .badge{display:inline-flex;align-items:center;gap:4px;border-radius:20px;padding:5px 14px;font-size:12px;font-weight:700;}
  .badge-status{background:${status.bg};color:${status.color};}
  .badge-type{background:rgba(255,255,255,0.2);color:#fff;}
  .badge-bond{background:#fef3c7;color:#d97706;}
  .badge-tag{background:rgba(255,255,255,0.15);color:#fff;font-size:11px;}
  .body{padding:28px 32px;}
  .kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:28px;}
  .kpi{background:#f8fafc;border-radius:12px;padding:14px;border-left:4px solid #0e4e96;}
  .kpi .kpi-label{font-size:10px;color:#64748B;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;}
  .kpi .kpi-value{font-size:18px;font-weight:800;color:#0e4e96;}
  .section{margin-bottom:24px;}
  .section h2{font-size:14px;font-weight:700;color:#0e4e96;text-transform:uppercase;letter-spacing:1px;margin-bottom:12px;padding-bottom:6px;border-bottom:2px solid #e0f2fe;}
  p{font-size:13px;line-height:1.75;color:#374151;}
  .req-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
  .req-item{display:flex;align-items:center;gap:8px;background:#f0fdf4;border-radius:8px;padding:8px 12px;}
  .req-dot{width:8px;height:8px;border-radius:50%;background:#059669;flex-shrink:0;}
  .req-text{font-size:13px;color:#1e293b;}
  .contact-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
  .contact-item{background:#f8fafc;border-radius:10px;padding:12px;}
  .contact-label{font-size:10px;color:#64748B;text-transform:uppercase;letter-spacing:1px;margin-bottom:3px;}
  .contact-value{font-size:13px;font-weight:600;color:#1e293b;}
  .bond-alert{background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;padding:14px;display:flex;align-items:center;gap:10px;margin-top:10px;}
  .bond-icon{font-size:20px;}
  .bond-text{font-size:13px;color:#d97706;font-weight:600;}
  .footer{background:#f1f5f9;padding:16px 32px;text-align:center;font-size:11px;color:#64748B;margin-top:20px;}
  .footer strong{color:#0e4e96;}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
</style></head><body>
<div class="header">
  <div class="header-top">
    <div>
      <div class="brand">JOBENG · Tenders Management</div>
    </div>
    <div class="tender-ref">
      <div class="label">TENDER REF</div>
      <div class="value">${(t.id||'').toString().toUpperCase()}</div>
    </div>
  </div>
  <h1>${t.title||'Untitled Tender'}</h1>
  <div class="org">${t.organisation||t.postedBy||'—'}</div>
  <div class="badges">
    <span class="badge badge-status">${status.label}</span>
    <span class="badge badge-type">${(t.type||'external').toUpperCase()}</span>
    ${t.bond?'<span class="badge badge-bond">⚡ BOND REQUIRED</span>':''}
    ${(t.tags||[]).map(tag=>`<span class="badge badge-tag">${tag.toUpperCase()}</span>`).join('')}
  </div>
</div>
<div class="body">
  <div class="kpi-grid">
    <div class="kpi"><div class="kpi-label">Budget Min</div><div class="kpi-value">${fmtGBP(t.budgetMin)}</div></div>
    <div class="kpi"><div class="kpi-label">Budget Max</div><div class="kpi-value">${fmtGBP(t.budgetMax)}</div></div>
    <div class="kpi"><div class="kpi-label">Deadline</div><div class="kpi-value">${fmtDate(t.deadline)}</div></div>
    <div class="kpi"><div class="kpi-label">Duration</div><div class="kpi-value">${t.duration||'TBC'}</div></div>
  </div>
  <div class="section">
    <h2>Description</h2>
    <p>${t.description||'No description provided.'}</p>
  </div>
  ${t.requirements?.length?`<div class="section"><h2>Requirements</h2><div class="req-grid">${t.requirements.map(r=>`<div class="req-item"><div class="req-dot"></div><div class="req-text">${r}</div></div>`).join('')}</div></div>`:''}
  ${(t.contactEmail||t.contactPhone||t.location)?`<div class="section"><h2>Contact & Location</h2><div class="contact-grid">${t.location?`<div class="contact-item"><div class="contact-label">Location</div><div class="contact-value">${t.location}</div></div>`:''}${t.contactEmail?`<div class="contact-item"><div class="contact-label">Email</div><div class="contact-value">${t.contactEmail}</div></div>`:''}${t.contactPhone?`<div class="contact-item"><div class="contact-label">Phone</div><div class="contact-value">${t.contactPhone}</div></div>`:''}${t.category?`<div class="contact-item"><div class="contact-label">Category</div><div class="contact-value">${t.category}</div></div>`:''}</div></div>`:''}
  ${t.bond?`<div class="bond-alert"><div class="bond-icon">⚠️</div><div class="bond-text">Performance Bond Required: ${fmtGBP(t.bondAmount)} · Bidders must confirm bond capability in submission</div></div>`:''}
</div>
<div class="footer">Generated by <strong>JOBENG Tenders Management</strong> · ${new Date().toLocaleDateString('en-GB',{weekday:'long',year:'numeric',month:'long',day:'numeric'})} · Document is confidential</div>
</body></html>`;
  };

  const handlePreviewPDF = async (t) => {
    try {
      const html = buildHTML(t);
      const { uri } = await Print.printToFileAsync({ html, base64:false });
      await Sharing.shareAsync(uri, { mimeType:'application/pdf', dialogTitle:`Tender – ${t.title}` });
    } catch(e) { Alert.alert('Error','PDF generation failed: '+e.message); }
  };

  const handlePrint = async (t) => {
    try {
      const html = buildHTML(t);
      await Print.printAsync({ html });
    } catch(e) { Alert.alert('Error','Print failed: '+e.message); }
  };

  const handleShare = async (t) => {
    try {
      await Share.share({message:`Tender: ${t.title}\nOrg: ${t.organisation||t.postedBy}\nBudget: ${fmtGBP(t.budgetMin)}–${fmtGBP(t.budgetMax)}\nDeadline: ${fmtDate(t.deadline)}\nLocation: ${t.location||'—'}\n\nRef: ${t.id}`});
    } catch {}
  };

  // ─── TENDER PREVIEW MODAL ─────────────────────────────────────────────────
  const TenderPreviewModal = () => {
    if(!previewTender) return null;
    const t = previewTender;
    const dl = daysLeft(t.deadline);
    const bidCount = submittedBids.filter(b=>b.tenderId===t.id).length;
    const status = STATUS_CONFIG[(t.status||'draft').toLowerCase()]||STATUS_CONFIG.draft;
    const isOwn = myTenders.some(m=>m.id===t.id);
    return (
      <Modal visible={showPreview} animationType="slide" onRequestClose={()=>setShowPreview(false)}>
        <SafeAreaView style={{flex:1,backgroundColor:'#f8fafc'}}>
          {/* Header */}
          <View style={ss.pvHdr}>
            <TouchableOpacity onPress={()=>setShowPreview(false)} style={ss.pvHdrBtn}>
              <MaterialCommunityIcons name="arrow-left" size={22} color={P}/>
            </TouchableOpacity>
            <Text style={ss.pvHdrTitle} numberOfLines={1}>Tender Preview</Text>
            <View style={{flexDirection:'row',gap:8}}>
              <TouchableOpacity onPress={()=>toggleSave(t.id)} style={ss.pvHdrBtn}>
                <MaterialCommunityIcons name={savedIds.includes(t.id)?'bookmark':'bookmark-outline'} size={22} color={savedIds.includes(t.id)?Y:P2}/>
              </TouchableOpacity>
              <TouchableOpacity onPress={()=>handleShare(t)} style={ss.pvHdrBtn}>
                <MaterialCommunityIcons name="share-variant" size={22} color={P}/>
              </TouchableOpacity>
            </View>
          </View>

          <ScrollView contentContainerStyle={{paddingBottom:40}}>
            {/* Hero Banner */}
            <View style={ss.pvHero}>
              <View style={ss.pvHeroBadges}>
                <View style={[ss.pvBadge,{backgroundColor:t.type==='internal'?P:TEAL}]}>
                  <Text style={ss.pvBadgeTxt}>{(t.type||'external').toUpperCase()}</Text>
                </View>
                <View style={[ss.pvBadge,{backgroundColor:status.bg,borderWidth:1,borderColor:status.color}]}>
                  <Text style={[ss.pvBadgeTxt,{color:status.color}]}>{status.label}</Text>
                </View>
                {t.bond&&<View style={[ss.pvBadge,{backgroundColor:'#fef3c7',borderWidth:1,borderColor:'#d97706'}]}><Text style={[ss.pvBadgeTxt,{color:'#d97706'}]}>⚡ BOND</Text></View>}
              </View>
              <Text style={ss.pvTitle}>{t.title}</Text>
              <Text style={ss.pvOrg}><MaterialCommunityIcons name="office-building" size={14} color="rgba(255,255,255,0.8)"/> {t.organisation||t.postedBy||'—'}</Text>
              <View style={{flexDirection:'row',flexWrap:'wrap',gap:6,marginTop:10}}>
                {t.tags?.map(tag=>(
                  <View key={tag} style={ss.pvTag}><Text style={ss.pvTagTxt}>{tag.toUpperCase()}</Text></View>
                ))}
              </View>
            </View>

            {/* KPI Grid */}
            <View style={ss.pvSection}>
              <View style={ss.pvKpiGrid}>
                <View style={[ss.pvKpi,{borderLeftColor:P}]}>
                  <Text style={ss.pvKpiLabel}>BUDGET MIN</Text>
                  <Text style={ss.pvKpiValue}>{fmtGBP(t.budgetMin)}</Text>
                </View>
                <View style={[ss.pvKpi,{borderLeftColor:'#059669'}]}>
                  <Text style={ss.pvKpiLabel}>BUDGET MAX</Text>
                  <Text style={[ss.pvKpiValue,{color:'#059669'}]}>{fmtGBP(t.budgetMax)}</Text>
                </View>
                <View style={[ss.pvKpi,{borderLeftColor:dl!=null&&dl<=7?'#dc2626':'#d97706'}]}>
                  <Text style={ss.pvKpiLabel}>DEADLINE</Text>
                  <Text style={[ss.pvKpiValue,{color:dl!=null&&dl<=7?'#dc2626':'#d97706',fontSize:13}]}>{dl!=null?(dl<=0?'CLOSED':`${dl} days left`):'—'}</Text>
                </View>
                <View style={[ss.pvKpi,{borderLeftColor:P2}]}>
                  <Text style={ss.pvKpiLabel}>DURATION</Text>
                  <Text style={[ss.pvKpiValue,{color:P2,fontSize:13}]}>{t.duration||'TBC'}</Text>
                </View>
              </View>
            </View>

            {/* Dates / Meta */}
            <View style={ss.pvSection}>
              <Text style={ss.pvSectionTitle}>Tender Details</Text>
              <View style={ss.pvMetaGrid}>
                <View style={ss.pvMetaBox}><Text style={ss.pvMetaLabel}>Posted</Text><Text style={ss.pvMetaVal}>{fmtDate(t.createdDate)}</Text></View>
                <View style={ss.pvMetaBox}><Text style={ss.pvMetaLabel}>Category</Text><Text style={ss.pvMetaVal}>{t.category||'—'}</Text></View>
                <View style={ss.pvMetaBox}><Text style={ss.pvMetaLabel}>Location</Text><Text style={ss.pvMetaVal}>{t.location||'—'}</Text></View>
                <View style={ss.pvMetaBox}><Text style={ss.pvMetaLabel}>Views</Text><Text style={ss.pvMetaVal}>{t.views||0}</Text></View>
                {t.type==='internal'&&<View style={ss.pvMetaBox}><Text style={ss.pvMetaLabel}>Bids Received</Text><Text style={[ss.pvMetaVal,{color:P2,fontWeight:'700'}]}>{t.applicants||bidCount}</Text></View>}
                <View style={ss.pvMetaBox}><Text style={ss.pvMetaLabel}>Currency</Text><Text style={ss.pvMetaVal}>{t.currency||'GBP'}</Text></View>
              </View>
            </View>

            {/* Description */}
            {t.description&&(
              <View style={ss.pvSection}>
                <Text style={ss.pvSectionTitle}>Description</Text>
                <Text style={ss.pvDesc}>{t.description}</Text>
              </View>
            )}

            {/* Requirements */}
            {t.requirements?.length>0&&(
              <View style={ss.pvSection}>
                <Text style={ss.pvSectionTitle}>Requirements ({t.requirements.length})</Text>
                <View style={ss.pvReqGrid}>
                  {t.requirements.map((r,i)=>(
                    <View key={i} style={ss.pvReqItem}>
                      <View style={ss.pvReqDot}/>
                      <Text style={ss.pvReqTxt}>{r}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Bond Alert */}
            {t.bond&&(
              <View style={ss.pvSection}>
                <View style={ss.pvBondAlert}>
                  <MaterialCommunityIcons name="alert-circle" size={22} color="#d97706"/>
                  <View style={{flex:1,marginLeft:10}}>
                    <Text style={ss.pvBondTitle}>Performance Bond Required</Text>
                    <Text style={ss.pvBondAmount}>Amount: {fmtGBP(t.bondAmount)}</Text>
                    <Text style={ss.pvBondNote}>Bidders must confirm bond capability in their submission.</Text>
                  </View>
                </View>
              </View>
            )}

            {/* Contact */}
            {(t.contactEmail||t.contactPhone)&&(
              <View style={ss.pvSection}>
                <Text style={ss.pvSectionTitle}>Contact Information</Text>
                <View style={ss.pvMetaGrid}>
                  {t.contactEmail&&<View style={ss.pvMetaBox}><Text style={ss.pvMetaLabel}>Email</Text><Text style={[ss.pvMetaVal,{color:P2}]}>{t.contactEmail}</Text></View>}
                  {t.contactPhone&&<View style={ss.pvMetaBox}><Text style={ss.pvMetaLabel}>Phone</Text><Text style={ss.pvMetaVal}>{t.contactPhone}</Text></View>}
                </View>
              </View>
            )}

            {/* Documents placeholder */}
            <View style={ss.pvSection}>
              <Text style={ss.pvSectionTitle}>Documents</Text>
              {(t.documents?.length>0) ? (
                t.documents.map((d,i)=>(
                  <View key={i} style={ss.pvDocRow}>
                    <MaterialCommunityIcons name="file-pdf-box" size={20} color="#dc2626"/>
                    <Text style={ss.pvDocTxt}>{d.name||d}</Text>
                  </View>
                ))
              ) : (
                <View style={ss.pvDocRow}>
                  <MaterialCommunityIcons name="file-document-outline" size={20} color="#94a3b8"/>
                  <Text style={[ss.pvDocTxt,{color:'#94a3b8'}]}>Tender_Specification.pdf</Text>
                </View>
              )}
            </View>

            {/* Own Tender Actions */}
            {isOwn&&(
              <View style={ss.pvSection}>
                <Text style={ss.pvSectionTitle}>Manage This Tender</Text>
                <View style={{flexDirection:'row',gap:10,flexWrap:'wrap'}}>
                  <TouchableOpacity style={ss.pvMgmtBtn} onPress={()=>{setShowPreview(false);setTimeout(()=>{setEditingTender(t);setShowForm(true);},300);}}>
                    <MaterialCommunityIcons name="pencil" size={16} color={P}/>
                    <Text style={[ss.pvMgmtTxt,{color:P}]}>Edit</Text>
                  </TouchableOpacity>
                  {(t.status==='open'||t.status==='published')&&<TouchableOpacity style={ss.pvMgmtBtn} onPress={()=>closeTender(t)}><MaterialCommunityIcons name="close-circle" size={16} color="#dc2626"/><Text style={[ss.pvMgmtTxt,{color:'#dc2626'}]}>Close</Text></TouchableOpacity>}
                  <TouchableOpacity style={ss.pvMgmtBtn} onPress={()=>{setShowPreview(false);deleteTender(t.id);}}><MaterialCommunityIcons name="delete" size={16} color="#dc2626"/><Text style={[ss.pvMgmtTxt,{color:'#dc2626'}]}>Delete</Text></TouchableOpacity>
                </View>
              </View>
            )}
          </ScrollView>

          {/* Action Footer */}
          <View style={ss.pvFooter}>
            <TouchableOpacity style={ss.pvFootBtnSec} onPress={()=>handlePrint(t)}>
              <MaterialCommunityIcons name="printer" size={18} color={P}/>
              <Text style={[ss.pvFootBtnSecTxt,{color:P}]}>Print</Text>
            </TouchableOpacity>
            <TouchableOpacity style={ss.pvFootBtnSec} onPress={()=>handlePreviewPDF(t)}>
              <MaterialCommunityIcons name="file-pdf-box" size={18} color="#dc2626"/>
              <Text style={[ss.pvFootBtnSecTxt,{color:'#dc2626'}]}>PDF</Text>
            </TouchableOpacity>
            {t.type==='external'&&(
              <TouchableOpacity style={ss.pvFootBtnPri} onPress={()=>{setShowPreview(false);setTimeout(()=>navigation?.navigate('TenderBidQuotation',{tab:'bids',tenderId:t.id,tenderTitle:t.title,organisation:t.organisation}),300);}}>
                <MaterialCommunityIcons name="send" size={18} color="#fff"/>
                <Text style={ss.pvFootBtnPriTxt}>Create Bid</Text>
              </TouchableOpacity>
            )}
            {t.type==='internal'&&(
              <TouchableOpacity style={ss.pvFootBtnPri} onPress={()=>{setShowPreview(false);setTimeout(()=>navigation?.navigate('TenderBidQuotation',{tab:'bids'}),300);}}>
                <MaterialCommunityIcons name="eye" size={18} color="#fff"/>
                <Text style={ss.pvFootBtnPriTxt}>View Bids</Text>
              </TouchableOpacity>
            )}
          </View>
        </SafeAreaView>
      </Modal>
    );
  };

  // ─── TENDER FORM MODAL ────────────────────────────────────────────────────
  const TenderFormModal = () => {
    const blank={title:'',type:'internal',category:'engineering',location:'',budgetMin:'',budgetMax:'',currency:'GBP',deadline:'',duration:'',description:'',contactEmail:'',contactPhone:'',tags:'',bond:false,bondAmount:'',requirements:[],documents:[]};
    const [form,setForm]=useState(editingTender||blank);
    const [newReq,setNewReq]=useState('');
    const set=(k,v)=>setForm(f=>({...f,[k]:v}));
    const addReq=()=>{ if(newReq.trim()){set('requirements',[...(form.requirements||[]),newReq.trim()]);setNewReq('');} };
    const pickDoc = async () => {
      try {
        const res=await DocumentPicker.getDocumentAsync({type:'*/*',copyToCacheDirectory:true,multiple:true});
        if(!res.canceled && res.assets?.length>0){
          const newDocs=res.assets.map(a=>({name:a.name,uri:a.uri,size:a.size}));
          set('documents',[...(form.documents||[]),...newDocs]);
        }
      } catch {}
    };
    return (
      <Modal visible={showForm} animationType="slide" onRequestClose={()=>setShowForm(false)}>
        <SafeAreaView style={{flex:1,backgroundColor:'#f8fafc'}}>
          <View style={ss.formHdr}>
            <TouchableOpacity onPress={()=>setShowForm(false)}><MaterialCommunityIcons name="close" size={24} color={P}/></TouchableOpacity>
            <Text style={ss.formHdrTitle}>{editingTender?'Edit Tender':'Create New Tender'}</Text>
            <View style={{width:24}}/>
          </View>
          <KeyboardAvoidingView style={{flex:1}} behavior={Platform.OS==='ios'?'padding':'height'}>
            <ScrollView contentContainerStyle={{padding:16,paddingBottom:40}}>
              {/* Tender Type */}
              <Text style={ss.sectionLbl}>Tender Type</Text>
              <View style={{flexDirection:'row',gap:10,marginBottom:14}}>
                {[['internal','Internal (for Subcontractors)','tools'],['external','External (for Clients)','domain']].map(([v,l,ic])=>(
                  <TouchableOpacity key={v} style={[ss.typeCard,form.type===v&&ss.typeCardActive]} onPress={()=>set('type',v)}>
                    <MaterialCommunityIcons name={ic==='tools'?'wrench':'domain'} size={20} color={form.type===v?P:'#94a3b8'}/>
                    <Text style={[ss.typeCardTxt,form.type===v&&{color:P,fontWeight:'700'}]}>{l}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={ss.sectionLbl}>Basic Information</Text>
              <Text style={ss.lbl}>Tender Title *</Text>
              <TextInput style={ss.inp} value={form.title} onChangeText={v=>set('title',v)} placeholder="e.g. Electrical Subcontracting for Project X" placeholderTextColor="#94a3b8"/>

              <Text style={ss.lbl}>Category</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:12}}>
                <View style={{flexDirection:'row',gap:8}}>
                  {CATEGORIES.filter(c=>c.id!=='all').map(c=>(
                    <TouchableOpacity key={c.id} style={[ss.chip,form.category===c.id&&ss.chipActive]} onPress={()=>set('category',c.id)}>
                      <Text style={[ss.chipTxt,form.category===c.id&&ss.chipTxtActive]}>{c.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>

              <Text style={ss.lbl}>Location</Text>
              <TextInput style={ss.inp} value={form.location} onChangeText={v=>set('location',v)} placeholder="e.g. London, UK" placeholderTextColor="#94a3b8"/>

              <Text style={ss.sectionLbl}>Budget & Timeline</Text>
              <View style={ss.row2}>
                <View style={{flex:1}}><Text style={ss.lbl}>Budget Min (£)</Text><TextInput style={ss.inp} value={String(form.budgetMin||'')} onChangeText={v=>set('budgetMin',v)} keyboardType="numeric" placeholder="0" placeholderTextColor="#94a3b8"/></View>
                <View style={{flex:1}}><Text style={ss.lbl}>Budget Max (£)</Text><TextInput style={ss.inp} value={String(form.budgetMax||'')} onChangeText={v=>set('budgetMax',v)} keyboardType="numeric" placeholder="0" placeholderTextColor="#94a3b8"/></View>
              </View>
              <View style={ss.row2}>
                <View style={{flex:1}}><Text style={ss.lbl}>Deadline (YYYY-MM-DD)</Text><TextInput style={ss.inp} value={form.deadline} onChangeText={v=>set('deadline',v)} placeholder="2025-12-31" placeholderTextColor="#94a3b8"/></View>
                <View style={{flex:1}}><Text style={ss.lbl}>Duration</Text><TextInput style={ss.inp} value={form.duration} onChangeText={v=>set('duration',v)} placeholder="e.g. 6 months" placeholderTextColor="#94a3b8"/></View>
              </View>

              <Text style={ss.sectionLbl}>Description</Text>
              <TextInput style={[ss.inp,{height:100,textAlignVertical:'top'}]} value={form.description} onChangeText={v=>set('description',v)} multiline placeholder="Provide a full description of the scope of works, specifications and any key information..." placeholderTextColor="#94a3b8"/>

              <Text style={ss.sectionLbl}>Contact Information</Text>
              <Text style={ss.lbl}>Contact Email</Text>
              <TextInput style={ss.inp} value={form.contactEmail} onChangeText={v=>set('contactEmail',v)} keyboardType="email-address" placeholder="tender@yourcompany.co.uk" placeholderTextColor="#94a3b8"/>
              <Text style={ss.lbl}>Contact Phone</Text>
              <TextInput style={ss.inp} value={form.contactPhone} onChangeText={v=>set('contactPhone',v)} keyboardType="phone-pad" placeholder="+44 20 0000 0000" placeholderTextColor="#94a3b8"/>

              <Text style={ss.sectionLbl}>Tags</Text>
              <TextInput style={ss.inp} value={form.tags} onChangeText={v=>set('tags',v)} placeholder="urgent, public-sector, heritage (comma separated)" placeholderTextColor="#94a3b8"/>

              <Text style={ss.sectionLbl}>Performance Bond</Text>
              <View style={ss.switchRow}>
                <Text style={ss.lbl}>Bond Required</Text>
                <Switch value={form.bond} onValueChange={v=>set('bond',v)} trackColor={{true:P}} thumbColor={form.bond?P2:'#cbd5e1'}/>
              </View>
              {form.bond&&<><Text style={ss.lbl}>Bond Amount (£)</Text><TextInput style={ss.inp} value={String(form.bondAmount||'')} onChangeText={v=>set('bondAmount',v)} keyboardType="numeric" placeholder="10000" placeholderTextColor="#94a3b8"/></>}

              {/* Requirements */}
              <View style={ss.secHdrRow}>
                <Text style={ss.sectionLbl}>Requirements ({form.requirements?.length||0})</Text>
                <TouchableOpacity style={ss.addMiniBtn} onPress={addReq}><MaterialCommunityIcons name="plus" size={14} color="#fff"/><Text style={ss.addMiniBtnTxt}>Add</Text></TouchableOpacity>
              </View>
              <View style={{flexDirection:'row',gap:8,marginBottom:8}}>
                <TextInput style={[ss.inp,{flex:1,marginBottom:0}]} value={newReq} onChangeText={setNewReq} placeholder="e.g. NICEIC certified, 5+ years experience..." placeholderTextColor="#94a3b8" onSubmitEditing={addReq} returnKeyType="done"/>
              </View>
              {form.requirements?.map((r,i)=>(
                <View key={i} style={ss.listRow}>
                  <MaterialCommunityIcons name="check-circle-outline" size={16} color="#059669"/>
                  <Text style={{flex:1,marginLeft:8,fontSize:13,color:'#1e293b'}}>{r}</Text>
                  <TouchableOpacity onPress={()=>set('requirements',form.requirements.filter((_,j)=>j!==i))}><MaterialCommunityIcons name="close" size={16} color="#dc2626"/></TouchableOpacity>
                </View>
              ))}

              {/* Documents */}
              <View style={ss.secHdrRow}>
                <Text style={ss.sectionLbl}>Documents ({form.documents?.length||0})</Text>
                <TouchableOpacity style={ss.addMiniBtn} onPress={pickDoc}><MaterialCommunityIcons name="upload" size={14} color="#fff"/><Text style={ss.addMiniBtnTxt}>Upload</Text></TouchableOpacity>
              </View>
              <TouchableOpacity style={ss.attachBox} onPress={pickDoc}>
                <MaterialCommunityIcons name="paperclip" size={20} color={P}/>
                <Text style={ss.attachTxt}>Tap to attach tender specification, plans or drawings</Text>
              </TouchableOpacity>
              {form.documents?.map((d,i)=>(
                <View key={i} style={ss.listRow}>
                  <MaterialCommunityIcons name="file-document" size={16} color={P}/>
                  <Text style={{flex:1,marginLeft:8,fontSize:13,color:P}}>{d.name||d}</Text>
                  <TouchableOpacity onPress={()=>set('documents',form.documents.filter((_,j)=>j!==i))}><MaterialCommunityIcons name="close" size={16} color="#dc2626"/></TouchableOpacity>
                </View>
              ))}
            </ScrollView>
          </KeyboardAvoidingView>
          <View style={ss.formFooter}>
            <TouchableOpacity style={ss.formBtnSec} onPress={()=>{const d={...form,id:editingTender?.id,tags:typeof form.tags==='string'?form.tags.split(',').map(s=>s.trim()).filter(Boolean):form.tags};saveTender(d,false);setShowForm(false);}}>
              <Text style={[ss.formBtnSecTxt,{color:P}]}>Save Draft</Text>
            </TouchableOpacity>
            <TouchableOpacity style={ss.formBtnPri} onPress={()=>{const d={...form,id:editingTender?.id,tags:typeof form.tags==='string'?form.tags.split(',').map(s=>s.trim()).filter(Boolean):form.tags};saveTender(d,true);setShowForm(false);}}>
              <MaterialCommunityIcons name="publish" size={18} color="#fff"/>
              <Text style={ss.formBtnPriTxt}>Publish Tender</Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>
    );
  };

  // ─── FILTER MODAL ─────────────────────────────────────────────────────────
  const FilterModal = () => {
    const [f,setF]=useState(filters);
    const setFv=(k,v)=>setF(x=>({...x,[k]:v}));
    return (
      <Modal visible={showFilter} animationType="slide" transparent onRequestClose={()=>setShowFilter(false)}>
        <View style={ss.sheetOverlay}>
          <View style={ss.sheet}>
            <View style={ss.sheetHdr}><Text style={ss.sheetTitle}>Filter Tenders</Text><TouchableOpacity onPress={()=>setShowFilter(false)}><MaterialCommunityIcons name="close" size={22} color="#64748B"/></TouchableOpacity></View>
            <ScrollView style={{maxHeight:460}}>
              <Text style={ss.lbl}>Category</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:12}}>
                <View style={{flexDirection:'row',gap:8,paddingBottom:4}}>
                  {CATEGORIES.map(c=><TouchableOpacity key={c.id} style={[ss.chip,f.category===c.id&&ss.chipActive]} onPress={()=>setFv('category',c.id)}><Text style={[ss.chipTxt,f.category===c.id&&ss.chipTxtActive]}>{c.label}</Text></TouchableOpacity>)}
                </View>
              </ScrollView>
              <Text style={ss.lbl}>Type</Text>
              <View style={{flexDirection:'row',gap:8,marginBottom:12}}>
                {['all','internal','external'].map(t=><TouchableOpacity key={t} style={[ss.chip,f.type===t&&ss.chipActive]} onPress={()=>setFv('type',t)}><Text style={[ss.chipTxt,f.type===t&&ss.chipTxtActive]}>{t==='all'?'All Types':t.charAt(0).toUpperCase()+t.slice(1)}</Text></TouchableOpacity>)}
              </View>
              <Text style={ss.lbl}>Status</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:12}}>
                <View style={{flexDirection:'row',gap:8,paddingBottom:4}}>
                  {['all','open','published','draft','under_review','awarded','closed'].map(s=>(
                    <TouchableOpacity key={s} style={[ss.chip,f.status===s&&ss.chipActive]} onPress={()=>setFv('status',s)}>
                      <Text style={[ss.chipTxt,f.status===s&&ss.chipTxtActive]}>{s==='all'?'All Status':s.replace(/_/g,' ')}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
              <View style={ss.row2}>
                <View style={{flex:1}}><Text style={ss.lbl}>Budget Min (£)</Text><TextInput style={ss.inp} value={f.budgetMin} onChangeText={v=>setFv('budgetMin',v)} keyboardType="numeric" placeholder="0" placeholderTextColor="#94a3b8"/></View>
                <View style={{flex:1}}><Text style={ss.lbl}>Budget Max (£)</Text><TextInput style={ss.inp} value={f.budgetMax} onChangeText={v=>setFv('budgetMax',v)} keyboardType="numeric" placeholder="Any" placeholderTextColor="#94a3b8"/></View>
              </View>
              <Text style={ss.lbl}>Location</Text>
              <TextInput style={ss.inp} value={f.location} onChangeText={v=>setFv('location',v)} placeholder="e.g. London, Manchester..." placeholderTextColor="#94a3b8"/>
              <View style={ss.switchRow}><Text style={ss.lbl}>Bond Required Only</Text><Switch value={f.bond} onValueChange={v=>setFv('bond',v)} trackColor={{true:P}} thumbColor={f.bond?P2:'#cbd5e1'}/></View>
            </ScrollView>
            <View style={{flexDirection:'row',gap:10,paddingTop:12}}>
              <TouchableOpacity style={[ss.formBtnSec,{flex:1}]} onPress={()=>{setFilters({category:'all',status:'all',type:'all',budgetMin:'',budgetMax:'',location:'',bond:false});setShowFilter(false);}}><Text style={[ss.formBtnSecTxt,{color:'#64748B'}]}>Reset</Text></TouchableOpacity>
              <TouchableOpacity style={[ss.formBtnPri,{flex:2}]} onPress={()=>{setFilters(f);setShowFilter(false);}}><Text style={ss.formBtnPriTxt}>Apply Filters</Text></TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // ─── SORT MODAL ───────────────────────────────────────────────────────────
  const SortModal = () => (
    <Modal visible={showSort} animationType="slide" transparent onRequestClose={()=>setShowSort(false)}>
      <View style={ss.sheetOverlay}>
        <View style={ss.sheet}>
          <View style={ss.sheetHdr}><Text style={ss.sheetTitle}>Sort Tenders</Text><TouchableOpacity onPress={()=>setShowSort(false)}><MaterialCommunityIcons name="close" size={22} color="#64748B"/></TouchableOpacity></View>
          {[['deadline','⏰  Deadline (Soonest First)'],['budget_h','💷  Budget (Highest First)'],['budget_l','💷  Budget (Lowest First)'],['newest','🆕  Newest First'],['applicants','👥  Most Applicants']].map(([k,l])=>(
            <TouchableOpacity key={k} style={ss.sortRow} onPress={()=>{setSortBy(k);setShowSort(false);}}>
              <Text style={[ss.sortTxt,sortBy===k&&{color:P,fontWeight:'700'}]}>{l}</Text>
              {sortBy===k&&<MaterialCommunityIcons name="check-circle" size={20} color={P}/>}
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </Modal>
  );

  // ─── MAIN RENDER ──────────────────────────────────────────────────────────
  if (loading) return (
    <SafeAreaView style={ss.root}>
      <View style={[ss.header,{justifyContent:'center'}]}>
        <Text style={ss.headerTitle}>Tenders Management</Text>
      </View>
      <View style={ss.loadingBox}>
        <ActivityIndicator size="large" color={P}/>
        <Text style={ss.loadingTxt}>Loading tenders...</Text>
      </View>
    </SafeAreaView>
  );

  const activeFilters = Object.values(filters).some(v=>v&&v!=='all'&&v!==false&&v!=='');

  return (
    <SafeAreaView style={ss.root}>
      {/* Header */}
      <View style={ss.header}>
        <TouchableOpacity onPress={()=>navigation?.goBack()} style={ss.hdrBtn}>
          <Ionicons name="arrow-back" size={22} color="#fff"/>
        </TouchableOpacity>
        <View style={ss.hdrCenter}>
          {logoUrl
            ? <Image source={{uri:logoUrl}} style={ss.logo}/>
            : <MaterialCommunityIcons name="file-document-outline" size={26} color="#fff"/>
          }
          <Text style={ss.headerTitle}>Tenders Management</Text>
        </View>
        <TouchableOpacity onPress={()=>setShowSort(true)} style={ss.hdrBtn}>
          <MaterialCommunityIcons name="sort" size={22} color="#fff"/>
        </TouchableOpacity>
      </View>

      {/* Stats Banner */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={ss.statsBanner} contentContainerStyle={{paddingHorizontal:14,paddingVertical:10,gap:10}}>
        {[
          {l:'Total',v:allTenders.length,c:'#1e293b',ic:'file-multiple'},
          {l:'Open',v:totalOpen,c:'#059669',ic:'check-circle'},
          {l:'Internal',v:totalInternal,c:P,ic:'domain'},
          {l:'External',v:totalExternal,c:TEAL,ic:'earth'},
          {l:'Awarded',v:totalAwarded,c:'#7c3aed',ic:'trophy'},
          {l:'Pipeline',v:fmtGBP(totalBudget),c:P2,ic:'cash'},
        ].map((s,i)=>(
          <View key={i} style={[ss.statCard,{borderLeftColor:s.c}]}>
            <MaterialCommunityIcons name={s.ic} size={15} color={s.c}/>
            <Text style={[ss.statVal,{color:s.c}]}>{s.v}</Text>
            <Text style={ss.statLbl}>{s.l}</Text>
          </View>
        ))}
      </ScrollView>

      {/* Tab Bar */}
      <View style={ss.tabBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:12,paddingVertical:8,gap:8}}>
          {MAIN_TABS.map(tab=>(
            <TouchableOpacity key={tab} style={[ss.tab,activeTab===tab&&ss.tabActive]} onPress={()=>setActiveTab(tab)}>
              <Text style={[ss.tabTxt,activeTab===tab&&ss.tabTxtActive]}>{tab}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Search + Filter Row */}
      <View style={ss.searchRow}>
        <View style={ss.searchBox}>
          <Ionicons name="search" size={17} color="#94a3b8"/>
          <TextInput style={ss.searchInp} placeholder="Search tenders..." value={searchQuery} onChangeText={setSearchQuery} placeholderTextColor="#94a3b8"/>
          {searchQuery?<TouchableOpacity onPress={()=>setSearchQuery('')}><Ionicons name="close-circle" size={17} color="#94a3b8"/></TouchableOpacity>:null}
        </View>
        <TouchableOpacity style={[ss.iconBtn,activeFilters&&{backgroundColor:P+'22',borderWidth:1,borderColor:P}]} onPress={()=>setShowFilter(true)}>
          <MaterialCommunityIcons name="filter-variant" size={20} color={activeFilters?P:'#64748B'}/>
          {activeFilters&&<View style={ss.filterDot}/>}
        </TouchableOpacity>
        <TouchableOpacity style={ss.createBtn} onPress={()=>{setEditingTender(null);setShowForm(true);}}>
          <Ionicons name="add" size={18} color="#fff"/>
          <Text style={ss.createBtnTxt}>New Tender</Text>
        </TouchableOpacity>
      </View>

      {/* Category Strip */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={ss.catStrip} contentContainerStyle={{paddingHorizontal:14,paddingVertical:8,gap:8}}>
        {CATEGORIES.map(c=>(
          <TouchableOpacity key={c.id} style={[ss.catChip,filters.category===c.id&&ss.catChipActive]} onPress={()=>setFilters(f=>({...f,category:c.id}))}>
            <Text style={[ss.catChipTxt,filters.category===c.id&&ss.catChipTxtActive]}>{c.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Results count */}
      <View style={ss.resultsRow}>
        <Text style={ss.resultsTxt}>{filtered.length} tender{filtered.length!==1?'s':''} found</Text>
        {activeFilters&&<TouchableOpacity onPress={()=>setFilters({category:'all',status:'all',type:'all',budgetMin:'',budgetMax:'',location:'',bond:false})}><Text style={{fontSize:12,color:'#dc2626',fontWeight:'600'}}>Clear filters</Text></TouchableOpacity>}
      </View>

      {/* Tender List */}
      <FlatList
        data={filtered}
        keyExtractor={i=>i.id}
        contentContainerStyle={{padding:14,gap:14,paddingBottom:30}}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={P}/>}
        renderItem={({item})=>(
          <TenderCard
            item={item}
            onPress={(t)=>{setPreviewTender(t);setShowPreview(true);}}
            onSave={toggleSave}
            saved={savedIds.includes(item.id)}
            submittedBids={submittedBids}
          />
        )}
        ListEmptyComponent={(
          <View style={ss.empty}>
            <MaterialIcons name="search-off" size={52} color="#cbd5e1"/>
            <Text style={ss.emptyTitle}>No tenders found</Text>
            <Text style={ss.emptySub}>Try adjusting your search or filters</Text>
            <TouchableOpacity style={ss.emptyBtn} onPress={()=>{setEditingTender(null);setShowForm(true);}}>
              <Text style={ss.emptyBtnTxt}>Create a Tender</Text>
            </TouchableOpacity>
          </View>
        )}
      />

      {/* Modals */}
      <TenderPreviewModal/>
      <TenderFormModal/>
      <FilterModal/>
      <SortModal/>
    </SafeAreaView>
  );
};

// ─── STYLESHEET ───────────────────────────────────────────────────────────────
const ss = StyleSheet.create({
  root:            {flex:1,backgroundColor:'#f8fafc'},
  header:          {backgroundColor:P,paddingVertical:14,paddingHorizontal:16,flexDirection:'row',alignItems:'center',justifyContent:'space-between'},
  hdrBtn:          {width:36,height:36,borderRadius:18,backgroundColor:'rgba(255,255,255,0.15)',alignItems:'center',justifyContent:'center'},
  hdrCenter:       {flex:1,flexDirection:'row',alignItems:'center',justifyContent:'center',gap:8},
  logo:            {width:28,height:28,borderRadius:14,resizeMode:'cover'},
  headerTitle:     {color:'#fff',fontSize:17,fontWeight:'700'},
  loadingBox:      {flex:1,alignItems:'center',justifyContent:'center',gap:14},
  loadingTxt:      {color:'#64748B',fontSize:14},
  statsBanner:     {backgroundColor:'#fff',borderBottomWidth:1,borderBottomColor:'#e2e8f0',maxHeight:70},
  statCard:        {backgroundColor:'#f8fafc',borderRadius:10,padding:10,minWidth:90,alignItems:'center',gap:2,borderLeftWidth:3},
  statVal:         {fontSize:14,fontWeight:'800'},
  statLbl:         {fontSize:10,color:'#64748B',textAlign:'center'},
  tabBar:          {backgroundColor:'#fff',borderBottomWidth:1,borderBottomColor:'#e2e8f0'},
  tab:             {borderRadius:20,paddingHorizontal:16,paddingVertical:7,backgroundColor:'#f1f5f9'},
  tabActive:       {backgroundColor:P,shadowColor:P,shadowOpacity:0.3,shadowRadius:4,shadowOffset:{width:0,height:2},elevation:3},
  tabTxt:          {fontSize:13,color:'#64748B',fontWeight:'500'},
  tabTxtActive:    {color:'#fff',fontWeight:'700'},
  searchRow:       {flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingVertical:8,backgroundColor:'#fff',borderBottomWidth:1,borderBottomColor:'#f1f5f9',gap:8},
  searchBox:       {flex:1,flexDirection:'row',alignItems:'center',backgroundColor:'#f1f5f9',borderRadius:10,paddingHorizontal:10,height:38,gap:6},
  searchInp:       {flex:1,fontSize:14,color:'#1e293b'},
  iconBtn:         {width:38,height:38,borderRadius:10,backgroundColor:'#f1f5f9',alignItems:'center',justifyContent:'center'},
  filterDot:       {position:'absolute',top:7,right:7,width:8,height:8,borderRadius:4,backgroundColor:'#dc2626'},
  createBtn:       {flexDirection:'row',alignItems:'center',backgroundColor:Y,borderRadius:10,paddingHorizontal:14,paddingVertical:8,gap:4,shadowColor:Y,shadowOpacity:0.3,shadowRadius:4,shadowOffset:{width:0,height:2},elevation:2},
  createBtnTxt:    {color:'#fff',fontSize:13,fontWeight:'700'},
  catStrip:        {backgroundColor:'#fff',maxHeight:52,borderBottomWidth:1,borderBottomColor:'#f1f5f9'},
  catChip:         {borderRadius:18,paddingHorizontal:14,paddingVertical:6,backgroundColor:'#f1f5f9',borderWidth:1,borderColor:'transparent'},
  catChipActive:   {backgroundColor:P+'15',borderColor:P},
  catChipTxt:      {fontSize:12,color:'#64748B',fontWeight:'500'},
  catChipTxtActive:{color:P,fontWeight:'700'},
  resultsRow:      {flexDirection:'row',justifyContent:'space-between',alignItems:'center',paddingHorizontal:16,paddingVertical:6,backgroundColor:'#f8fafc'},
  resultsTxt:      {fontSize:12,color:'#94a3b8'},
  // Card
  card:            {backgroundColor:'#fff',borderRadius:14,overflow:'hidden',shadowColor:'#000',shadowOffset:{width:0,height:2},shadowOpacity:0.07,shadowRadius:6,elevation:3},
  cardTop:         {flexDirection:'row',justifyContent:'space-between',alignItems:'center',paddingHorizontal:12,paddingTop:10,paddingBottom:4},
  typeStripe:      {borderRadius:10,paddingHorizontal:10,paddingVertical:3},
  typeStripeTxt:   {color:'#fff',fontSize:10,fontWeight:'800',letterSpacing:0.5},
  saveBtn:         {padding:4},
  cardBody:        {padding:12,paddingTop:6},
  cardTitleRow:    {flexDirection:'row',alignItems:'flex-start',gap:8,marginBottom:5},
  cardTitle:       {flex:1,fontSize:14,fontWeight:'700',color:'#1e293b',lineHeight:20},
  cardOrg:         {fontSize:12,color:'#64748B',marginBottom:8},
  tagsRow:         {flexDirection:'row',flexWrap:'wrap',gap:5,marginBottom:10},
  tag:             {backgroundColor:'#e0f2fe',borderRadius:8,paddingHorizontal:8,paddingVertical:3,borderWidth:1,borderColor:'#bae6fd'},
  tagTxt:          {fontSize:10,color:'#0369a1',fontWeight:'600'},
  metaGrid:        {flexDirection:'row',flexWrap:'wrap',gap:10,marginBottom:10},
  metaItem:        {flexDirection:'row',alignItems:'center',gap:3},
  metaTxt:         {fontSize:12,color:'#64748B'},
  cardFooter:      {flexDirection:'row',justifyContent:'space-between',alignItems:'center',borderTopWidth:1,borderTopColor:'#f1f5f9',paddingTop:10},
  statsRow:        {flexDirection:'row',gap:12},
  viewBtn:         {flexDirection:'row',alignItems:'center',backgroundColor:P,borderRadius:8,paddingHorizontal:12,paddingVertical:6,gap:4},
  viewBtnTxt:      {color:'#fff',fontSize:12,fontWeight:'600'},
  pill:            {borderRadius:10,paddingHorizontal:8,paddingVertical:3,borderWidth:1},
  pillTxt:         {fontSize:10,fontWeight:'700'},
  empty:           {alignItems:'center',padding:40,gap:10},
  emptyTitle:      {fontSize:17,fontWeight:'700',color:'#94a3b8'},
  emptySub:        {fontSize:13,color:'#94a3b8',textAlign:'center'},
  emptyBtn:        {backgroundColor:P,borderRadius:10,paddingHorizontal:20,paddingVertical:10,marginTop:6},
  emptyBtnTxt:     {color:'#fff',fontSize:14,fontWeight:'600'},
  // Preview Modal
  pvHdr:           {flexDirection:'row',alignItems:'center',padding:14,backgroundColor:'#fff',borderBottomWidth:1,borderBottomColor:'#e2e8f0',gap:10},
  pvHdrBtn:        {width:34,height:34,borderRadius:17,backgroundColor:'#f1f5f9',alignItems:'center',justifyContent:'center'},
  pvHdrTitle:      {flex:1,fontSize:15,fontWeight:'700',color:'#1e293b'},
  pvHero:          {backgroundColor:P,padding:22,paddingBottom:26},
  pvHeroBadges:    {flexDirection:'row',flexWrap:'wrap',gap:8,marginBottom:12},
  pvBadge:         {borderRadius:20,paddingHorizontal:12,paddingVertical:5},
  pvBadgeTxt:      {fontSize:11,fontWeight:'700',color:'#fff'},
  pvTitle:         {fontSize:20,fontWeight:'800',color:'#fff',marginBottom:6,lineHeight:26},
  pvOrg:           {fontSize:13,color:'rgba(255,255,255,0.85)',marginBottom:6},
  pvTag:           {backgroundColor:'rgba(255,255,255,0.18)',borderRadius:10,paddingHorizontal:10,paddingVertical:4},
  pvTagTxt:        {fontSize:11,color:'#fff',fontWeight:'600'},
  pvSection:       {backgroundColor:'#fff',marginTop:10,padding:16},
  pvSectionTitle:  {fontSize:13,fontWeight:'700',color:P,textTransform:'uppercase',letterSpacing:1,marginBottom:12,paddingBottom:6,borderBottomWidth:1,borderBottomColor:'#e0f2fe'},
  pvKpiGrid:       {flexDirection:'row',gap:10},
  pvKpi:           {flex:1,backgroundColor:'#f8fafc',borderRadius:10,padding:10,borderLeftWidth:3},
  pvKpiLabel:      {fontSize:9,color:'#64748B',textTransform:'uppercase',letterSpacing:0.5,marginBottom:3},
  pvKpiValue:      {fontSize:15,fontWeight:'800',color:P},
  pvMetaGrid:      {flexDirection:'row',flexWrap:'wrap',gap:10},
  pvMetaBox:       {backgroundColor:'#f8fafc',borderRadius:10,padding:10,minWidth:'45%',flex:1},
  pvMetaLabel:     {fontSize:10,color:'#64748B',textTransform:'uppercase',letterSpacing:0.5},
  pvMetaVal:       {fontSize:13,fontWeight:'600',color:'#1e293b',marginTop:2},
  pvDesc:          {fontSize:14,color:'#475569',lineHeight:22},
  pvReqGrid:       {gap:8},
  pvReqItem:       {flexDirection:'row',alignItems:'center',backgroundColor:'#f0fdf4',borderRadius:8,padding:10,gap:8},
  pvReqDot:        {width:8,height:8,borderRadius:4,backgroundColor:'#059669'},
  pvReqTxt:        {flex:1,fontSize:13,color:'#1e293b'},
  pvBondAlert:     {flexDirection:'row',alignItems:'flex-start',backgroundColor:'#fff7ed',borderRadius:12,padding:14,borderWidth:1,borderColor:'#fed7aa'},
  pvBondTitle:     {fontSize:14,fontWeight:'700',color:'#d97706'},
  pvBondAmount:    {fontSize:13,color:'#92400e',marginTop:2,fontWeight:'600'},
  pvBondNote:      {fontSize:12,color:'#92400e',marginTop:3},
  pvDocRow:        {flexDirection:'row',alignItems:'center',backgroundColor:'#f8fafc',borderRadius:8,padding:10,marginBottom:8,gap:8},
  pvDocTxt:        {flex:1,fontSize:13,color:P,fontWeight:'500'},
  pvMgmtBtn:       {flexDirection:'row',alignItems:'center',backgroundColor:'#f1f5f9',borderRadius:10,paddingHorizontal:14,paddingVertical:8,gap:6},
  pvMgmtTxt:       {fontSize:13,fontWeight:'600'},
  pvFooter:        {flexDirection:'row',padding:14,backgroundColor:'#fff',borderTopWidth:1,borderTopColor:'#e2e8f0',gap:8},
  pvFootBtnSec:    {flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:'#f1f5f9',borderRadius:10,paddingVertical:12,paddingHorizontal:16,gap:5},
  pvFootBtnSecTxt: {fontSize:13,fontWeight:'600'},
  pvFootBtnPri:    {flex:1,flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:P,borderRadius:10,paddingVertical:12,gap:6},
  pvFootBtnPriTxt: {color:'#fff',fontSize:14,fontWeight:'700'},
  // Form Modal
  formHdr:         {flexDirection:'row',alignItems:'center',padding:16,backgroundColor:'#fff',borderBottomWidth:1,borderBottomColor:'#e2e8f0',gap:12},
  formHdrTitle:    {flex:1,fontSize:15,fontWeight:'700',color:'#1e293b'},
  formFooter:      {flexDirection:'row',padding:14,backgroundColor:'#fff',borderTopWidth:1,borderTopColor:'#e2e8f0',gap:10},
  formBtnSec:      {flex:1,alignItems:'center',justifyContent:'center',backgroundColor:'#f1f5f9',borderRadius:12,paddingVertical:13},
  formBtnSecTxt:   {fontSize:14,fontWeight:'600'},
  formBtnPri:      {flex:2,flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:P,borderRadius:12,paddingVertical:13,gap:6},
  formBtnPriTxt:   {color:'#fff',fontSize:14,fontWeight:'700'},
  typeCard:        {flex:1,backgroundColor:'#f8fafc',borderRadius:12,padding:12,alignItems:'center',gap:6,borderWidth:1.5,borderColor:'#e2e8f0'},
  typeCardActive:  {borderColor:P,backgroundColor:P+'08'},
  typeCardTxt:     {fontSize:12,color:'#64748B',textAlign:'center',fontWeight:'500'},
  sectionLbl:      {fontSize:12,fontWeight:'700',color:'#64748B',textTransform:'uppercase',letterSpacing:1,marginTop:16,marginBottom:8},
  lbl:             {fontSize:13,fontWeight:'600',color:'#374151',marginBottom:5,marginTop:4},
  inp:             {backgroundColor:'#f8fafc',borderWidth:1,borderColor:'#e2e8f0',borderRadius:10,paddingHorizontal:12,paddingVertical:10,fontSize:14,color:'#1e293b',marginBottom:4},
  row2:            {flexDirection:'row',gap:10,marginBottom:4},
  chip:            {borderRadius:18,paddingHorizontal:12,paddingVertical:6,backgroundColor:'#f1f5f9',borderWidth:1,borderColor:'#e2e8f0'},
  chipActive:      {backgroundColor:P+'15',borderColor:P},
  chipTxt:         {fontSize:12,color:'#64748B',fontWeight:'500'},
  chipTxtActive:   {color:P,fontWeight:'700'},
  switchRow:       {flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:4,marginTop:4},
  secHdrRow:       {flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginTop:16,marginBottom:8},
  addMiniBtn:      {flexDirection:'row',alignItems:'center',backgroundColor:P,borderRadius:8,paddingHorizontal:10,paddingVertical:5,gap:4},
  addMiniBtnTxt:   {color:'#fff',fontSize:12,fontWeight:'600'},
  listRow:         {flexDirection:'row',alignItems:'center',backgroundColor:'#f8fafc',borderRadius:8,padding:10,marginBottom:6,borderWidth:1,borderColor:'#e2e8f0'},
  attachBox:       {flexDirection:'row',alignItems:'center',justifyContent:'center',borderWidth:1.5,borderColor:P,borderStyle:'dashed',borderRadius:10,padding:14,marginBottom:8,gap:8},
  attachTxt:       {color:P,fontSize:13,flex:1},
  // Bottom sheets
  sheetOverlay:    {flex:1,backgroundColor:'rgba(0,0,0,0.4)',justifyContent:'flex-end'},
  sheet:           {backgroundColor:'#fff',borderTopLeftRadius:22,borderTopRightRadius:22,padding:20,paddingBottom:34},
  sheetHdr:        {flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:16},
  sheetTitle:      {fontSize:17,fontWeight:'700',color:'#1e293b'},
  sortRow:         {flexDirection:'row',justifyContent:'space-between',alignItems:'center',paddingVertical:14,borderBottomWidth:1,borderBottomColor:'#f1f5f9'},
  sortTxt:         {fontSize:14,color:'#1e293b'},
});

export default TendersManagementScreen;